import { forwardRef } from 'react'
import { ShoppingBag } from 'lucide-react'
import { formatRupiah, resolveUploadImageUrl } from '../lib/catalogUrl'
import { LazadaIcon, ShopeeIcon, TokopediaIcon } from './MarketplaceIcons'

export interface BrochureBrand {
  name: string
  slug?: string
  logoUrl?: string | null
  address?: string | null
  description?: string | null
  whatsapp?: string | null
  colorTheme?: string | null
  tokopediaUrl?: string | null
  shopeeUrl?: string | null
  lazadaUrl?: string | null
}

export interface BrochureProduct {
  id: number
  name: string
  price: number
  discountPrice?: number | null
  images?: string[] | null
  isActive?: boolean
}

interface CatalogBrochureTemplateProps {
  brand: BrochureBrand
  products: BrochureProduct[]
  qrDataUrl: string
  catalogUrl: string
}

const normalizePublicUrl = (raw?: string | null) => {
  if (!raw || typeof raw !== 'string') return null
  const t = raw.trim()
  if (!t) return null
  if (t.startsWith('http://') || t.startsWith('https://')) return t
  return `https://${t}`
}

export const CatalogBrochureTemplate = forwardRef<HTMLDivElement, CatalogBrochureTemplateProps>(
  function CatalogBrochureTemplate({ brand, products, qrDataUrl, catalogUrl }, ref) {
    const themeColor = brand.colorTheme || '#3b82f6'
    const activeProducts = products.filter((p) => p.isActive !== false)
    const brochureProducts = activeProducts.slice(0, 6)
    const tokopediaHref = normalizePublicUrl(brand.tokopediaUrl)
    const shopeeHref = normalizePublicUrl(brand.shopeeUrl)
    const lazadaHref = normalizePublicUrl(brand.lazadaUrl)

    return (
      <div className="fixed -left-[99999px] top-0 pointer-events-none" aria-hidden>
        <div
          ref={ref}
          className="w-[1080px] min-h-[1620px] p-10"
          style={{
            background: 'linear-gradient(160deg, #0f2b60 0%, #244b96 42%, #f6c957 100%)',
          }}
        >
          <div className="rounded-[30px] bg-white/95 border border-white/80 shadow-2xl p-8 min-h-[1540px] flex flex-col">
            <div className="flex items-start justify-between gap-6 mb-8">
              <div className="flex items-center gap-4">
                {brand.logoUrl ? (
                  <img
                    src={resolveUploadImageUrl(brand.logoUrl) || ''}
                    alt={brand.name}
                    className="w-20 h-20 rounded-2xl object-cover border border-slate-200"
                  />
                ) : (
                  <div
                    className="w-20 h-20 rounded-2xl flex items-center justify-center text-white text-3xl font-bold"
                    style={{ backgroundColor: themeColor }}
                  >
                    {brand.name?.charAt(0).toUpperCase()}
                  </div>
                )}
                <div>
                  <p className="text-xs font-bold tracking-[0.18em] text-[#0f2b60]">KATALOG PRODUK</p>
                  <h2 className="text-4xl font-black text-slate-900 mt-1">{brand.name}</h2>
                  <p className="text-sm text-slate-600 mt-2 max-w-[520px]">
                    {brand.description || 'Produk pilihan dengan kualitas terbaik untuk kebutuhan Anda.'}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs font-semibold text-slate-500">Alamat</p>
                <p className="text-sm font-semibold text-slate-800 max-w-[260px]">{brand.address || '-'}</p>
                {(tokopediaHref || shopeeHref || lazadaHref) && (
                  <div className="mt-3 flex flex-wrap justify-end gap-2">
                    {tokopediaHref && (
                      <span className="inline-flex rounded-xl border border-green-100/90 bg-white p-1 shadow-sm">
                        <TokopediaIcon className="size-9" />
                      </span>
                    )}
                    {shopeeHref && (
                      <span className="inline-flex rounded-xl border border-orange-100/90 bg-white p-1 shadow-sm">
                        <ShopeeIcon className="size-9" />
                      </span>
                    )}
                    {lazadaHref && (
                      <span className="inline-flex rounded-xl border border-blue-100/90 bg-white p-1 shadow-sm">
                        <LazadaIcon className="size-9" />
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>

            {brochureProducts.length > 0 ? (
              <div className="grid grid-cols-3 gap-5 flex-1">
                {brochureProducts.map((product) => (
                  <div
                    key={`brochure-${product.id}`}
                    className="rounded-2xl overflow-hidden border border-slate-200 bg-white shadow-sm"
                  >
                    <div className="h-[300px] bg-slate-100">
                      {product.images && product.images.length > 0 ? (
                        <img
                          src={resolveUploadImageUrl(product.images[0]) || ''}
                          alt={product.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <ShoppingBag size={54} className="text-slate-300" />
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <p className="text-base font-bold text-slate-900 line-clamp-2 min-h-[3rem]">{product.name}</p>
                      <p className="text-xl font-black mt-2" style={{ color: themeColor }}>
                        {formatRupiah(
                          product.discountPrice && product.discountPrice < product.price
                            ? product.discountPrice
                            : product.price
                        )}
                      </p>
                      {product.discountPrice && product.discountPrice < product.price && (
                        <p className="text-xs text-slate-400 line-through mt-1">{formatRupiah(product.price)}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-slate-50 py-16">
                <p className="text-lg text-slate-500">Belum ada produk aktif untuk ditampilkan</p>
              </div>
            )}

            <div className="mt-8 grid grid-cols-[1fr_auto] gap-6 items-center">
              <div className="rounded-2xl bg-[#0f2b60] text-white px-6 py-4">
                <p className="font-semibold text-lg">Hubungi kami untuk pemesanan</p>
                <p className="font-bold text-xl mt-1">{brand.whatsapp || '-'}</p>
                <p className="text-xs text-white/70 mt-2 break-all">{catalogUrl}</p>
              </div>
              <div className="flex flex-col items-center gap-2 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <img src={qrDataUrl} alt="QR Katalog" className="w-[140px] h-[140px]" />
                <p className="text-xs font-semibold text-[#0f2b60] text-center">Scan untuk<br />buka katalog</p>
              </div>
            </div>

            <p className="text-center text-xs text-slate-400 mt-6">Powered by UMKM Indonesia</p>
          </div>
        </div>
      </div>
    )
  }
)
